import { Helmet } from "react-helmet-async";

const BASE_URL = "https://whitefieldlegalservices.com";

interface SEOProps {
  /** Page title (appended with " | Whitefield Legal Services") */
  title: string;
  /** Meta description for search engines */
  description?: string;
  /** Meta keywords */
  keywords?: string;
  /** Canonical URL path (e.g. "/about") — full URL is built automatically */
  canonicalUrl?: string;
  /** Open Graph image URL */
  ogImage?: string;
  /** Open Graph type (default: "website") */
  ogType?: string;
  /** JSON-LD structured data — single object or array for @graph */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  /** Set true to add noindex,nofollow robots meta */
  noIndex?: boolean;
}

/**
 * Reusable SEO component that manages <head> tags via react-helmet-async.
 * Handles title, meta tags, canonical URL, Open Graph, Twitter Cards, and JSON-LD.
 */
export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  structuredData,
  noIndex = false,
}: SEOProps) {
  const fullTitle = `${title} | Whitefield Legal Services`;
  const fullCanonical = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : undefined;

  // Build JSON-LD: if array, wrap in @graph (strip individual @context)
  let jsonLd: string | undefined;
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      const graph = structuredData.map(({ "@context": _, ...rest }) => rest);
      jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    } else {
      jsonLd = JSON.stringify(structuredData);
    }
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>

      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Canonical */}
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}
      <meta property="og:site_name" content="Whitefield Legal Services" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{jsonLd}</script>
      )}
    </Helmet>
  );
}
